import React, { useState } from 'react';
import { MapPin, Mail, Phone, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { contactInfo } from '../data/mock';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock form submission with reCAPTCHA placeholder
    setTimeout(() => {
      toast.success('Message sent successfully! We\'ll respond within 24 hours.');
      e.target.reset();
      setIsSubmitting(false);
    }, 1500);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Us',
      content: contactInfo.email,
      link: `mailto:${contactInfo.email}`,
      color: 'from-[#2D5F3F] to-[#3A7D4A]'
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: contactInfo.phone,
      link: `tel:${contactInfo.phone}`,
      color: 'from-[#3A7D4A] to-[#2D5F3F]'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      content: contactInfo.whatsapp,
      link: `https://wa.me/${contactInfo.whatsapp.replace(/\s+/g, '')}`,
      color: 'from-[#D4AF37] to-[#C19B2C]'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      content: `${contactInfo.address.city} ${contactInfo.address.postcode}`,
      link: 'https://maps.google.com',
      color: 'from-[#2D5F3F] to-[#3A7D4A]'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Get In Touch</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Have questions? Want to partner, donate equipment, or learn more? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6 text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${method.color} rounded-2xl mb-4`}>
                    <method.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2D5F3F] mb-2">{method.title}</h3>
                  <a
                    href={method.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-[#2D5F3F] transition-colors duration-200 text-sm"
                  >
                    {method.content}
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8 md:p-12">
                  <h2 className="text-3xl font-bold text-[#2D5F3F] mb-2">Send Us a Message</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and we'll respond within 24 hours</p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact-name">Full Name *</Label>
                        <Input id="contact-name" name="name" required className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email Address *</Label>
                        <Input id="contact-email" name="email" type="email" required className="mt-2" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact-phone">Phone Number</Label>
                        <Input id="contact-phone" name="phone" type="tel" className="mt-2" />
                      </div>
                      <div>
                        <Label htmlFor="contact-subject">Subject *</Label>
                        <select
                          id="contact-subject"
                          name="subject"
                          required
                          className="w-full mt-2 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D5F3F]"
                        >
                          <option value="">Select a subject</option>
                          <option value="donation">Donation Inquiry</option>
                          <option value="equipment">Equipment Donation</option>
                          <option value="volunteer">Volunteer Opportunity</option>
                          <option value="partnership">Partnership Proposal</option>
                          <option value="media">Media/Press Inquiry</option>
                          <option value="other">General Question</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-message">Your Message *</Label>
                      <Textarea
                        id="contact-message"
                        name="message"
                        rows={6}
                        required
                        className="mt-2"
                        placeholder="Tell us how we can help..."
                      />
                    </div>

                    {/* reCAPTCHA Placeholder */}
                    <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="flex items-center justify-center space-x-3">
                        <input type="checkbox" id="recaptcha-mock" className="w-6 h-6" required />
                        <label htmlFor="recaptcha-mock" className="text-gray-600">
                          I'm not a robot (reCAPTCHA placeholder)
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Google reCAPTCHA will be integrated once domain is live
                      </p>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#2D5F3F] to-[#3A7D4A] hover:from-[#234A32] hover:to-[#2D5F3F] text-white font-semibold py-6 text-lg"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-2 border-[#2D5F3F] shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#2D5F3F] mb-6">Office Location</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-start space-x-3 mb-4">
                        <MapPin className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                        <div className="text-gray-700">
                          <p className="font-semibold">{contactInfo.address.line1}</p>
                          <p>{contactInfo.address.line2}</p>
                          <p>{contactInfo.address.city} {contactInfo.address.postcode}</p>
                          <p>{contactInfo.address.country}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-start space-x-3">
                        <Clock className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900 mb-2">Office Hours</p>
                          <p className="text-sm text-gray-700">Monday - Friday: 9:00 AM - 5:00 PM</p>
                          <p className="text-sm text-gray-700">Saturday: 10:00 AM - 2:00 PM</p>
                          <p className="text-sm text-gray-700">Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#D4AF37] shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#2D5F3F] mb-4">Charity Information</h3>
                  <div className="space-y-3 text-gray-700">
                    <p>
                      <span className="font-semibold">Registered Charity No:</span><br />
                      {contactInfo.charityNumber}
                    </p>
                    <p>
                      <span className="font-semibold">Registration:</span><br />
                      England & Wales
                    </p>
                    <a
                      href="https://www.gov.uk/find-charity-information"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4"
                    >
                      <Button variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white">
                        View on Charity Commission
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white border-none shadow-lg">
                <CardContent className="p-8">
                  <MessageCircle className="w-12 h-12 mb-4" />
                  <h3 className="text-xl font-bold mb-3">Urgent Equipment Donation?</h3>
                  <p className="text-gray-200 mb-4 text-sm">
                    If you have mobility equipment to donate urgently, please call or WhatsApp us directly for fastest response.
                  </p>
                  <a href={`https://wa.me/${contactInfo.whatsapp.replace(/\s+/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[#D4AF37] hover:bg-[#C19B2C] text-white">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp Us Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-gray-200">
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-semibold">Interactive Map</p>
            <p className="text-sm text-gray-500">Google Maps integration placeholder</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
